import type { ReactNode } from "react";

/**
 * The primitives every screen is built from.
 *
 * Small and few on purpose. A console needs about six shapes; shipping a
 * component library's worth of variants would mean most of them are never
 * used and none of them are quite right.
 */

const cx = (...parts: (string | false | null | undefined)[]) => parts.filter(Boolean).join(" ");

export function Card({
  children,
  className,
  padded = true,
}: {
  children: ReactNode;
  className?: string;
  padded?: boolean;
}) {
  return (
    <div
      className={cx(
        "rounded-[14px] border border-[var(--border)] bg-[var(--surface-1)]",
        "shadow-[var(--elevation-card)]",
        padded && "p-5",
        className,
      )}
    >
      {children}
    </div>
  );
}

/**
 * A headline figure.
 *
 * `tabular` is not decoration: without it a row of counts jitters as the
 * digits change, which on a live dashboard is constant and maddening.
 */
export function Stat({
  label,
  value,
  hint,
  tone = "default",
}: {
  label: string;
  value: string | number;
  hint?: string;
  tone?: "default" | "brand" | "ok" | "warn" | "danger";
}) {
  const toneClass = {
    default: "text-[var(--foreground)]",
    brand: "text-[var(--brand)]",
    ok: "text-[var(--ok)]",
    warn: "text-[var(--warn)]",
    danger: "text-[var(--danger)]",
  }[tone];

  return (
    <Card>
      <div className="text-[11px] font-medium uppercase tracking-[0.08em] text-[var(--faint)]">{label}</div>
      <div className={cx("tabular mt-2 text-3xl font-semibold leading-none", toneClass)}>{value}</div>
      {hint && <div className="mt-2 text-[13px] text-[var(--muted)]">{hint}</div>}
    </Card>
  );
}

export function Button({
  children,
  onClick,
  variant = "secondary",
  type = "button",
  disabled,
  className,
  title,
}: {
  children: ReactNode;
  onClick?: () => void;
  variant?: "primary" | "secondary" | "ghost" | "danger";
  type?: "button" | "submit";
  disabled?: boolean;
  className?: string;
  title?: string;
}) {
  const variants = {
    primary:
      "bg-[var(--brand)] text-[var(--brand-ink)] hover:brightness-110 shadow-[var(--elevation-glow)] font-semibold",
    secondary:
      "bg-[var(--surface-2)] text-[var(--foreground)] border border-[var(--border)] hover:border-[var(--border-strong)]",
    ghost: "text-[var(--muted)] hover:text-[var(--foreground)] hover:bg-[var(--surface-2)]",
    danger: "bg-transparent text-[var(--danger)] border border-[var(--danger)]/35 hover:bg-[var(--danger)]/10",
  }[variant];

  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-[10px] px-3.5 py-2 text-[13px]",
        // 150ms for feedback, transform and opacity only.
        "transition-[background-color,border-color,filter,opacity] duration-150",
        "disabled:cursor-not-allowed disabled:opacity-45",
        variants,
        className,
      )}
    >
      {children}
    </button>
  );
}

export function Badge({
  children,
  tone = "neutral",
}: {
  children: ReactNode;
  tone?: "neutral" | "ok" | "warn" | "danger" | "brand";
}) {
  const tones = {
    neutral: "bg-[var(--surface-2)] text-[var(--muted)] border-[var(--border)]",
    ok: "bg-[var(--ok)]/12 text-[var(--ok)] border-[var(--ok)]/25",
    warn: "bg-[var(--warn)]/12 text-[var(--warn)] border-[var(--warn)]/25",
    danger: "bg-[var(--danger)]/12 text-[var(--danger)] border-[var(--danger)]/25",
    brand: "bg-[var(--brand-soft)] text-[var(--brand)] border-[var(--brand)]/25",
  }[tone];

  return (
    <span
      className={cx(
        "inline-flex items-center gap-1.5 rounded-[6px] border px-2 py-0.5 text-[11px] font-medium",
        tones,
      )}
    >
      {children}
    </span>
  );
}

export function Field({
  label,
  hint,
  error,
  children,
}: {
  label: string;
  hint?: string;
  error?: string;
  children: ReactNode;
}) {
  return (
    <label className="block">
      <span className="mb-1.5 block text-[13px] font-medium text-[var(--foreground)]">{label}</span>
      {children}
      {/* The error replaces the hint rather than stacking, so the field never
          grows and pushes the form around as someone types. */}
      {error ? (
        <span className="mt-1.5 block text-[12px] text-[var(--danger)]">{error}</span>
      ) : hint ? (
        <span className="mt-1.5 block text-[12px] text-[var(--faint)]">{hint}</span>
      ) : null}
    </label>
  );
}

export const inputClass =
  "w-full rounded-[10px] border border-[var(--border)] bg-[var(--surface-2)] px-3 py-2 " +
  "text-[14px] text-[var(--foreground)] placeholder:text-[var(--faint)] " +
  "transition-colors duration-150 focus:border-[var(--brand)] focus:outline-none";

/**
 * Every list gets a designed empty state.
 *
 * An empty table with headers and no rows reads as broken. Saying what would
 * be here, and why it is not, is the difference between "nothing yet" and
 * "something failed".
 */
export function Empty({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return (
    <div className="flex flex-col items-center justify-center gap-2 px-6 py-14 text-center">
      <div className="text-[15px] font-medium text-[var(--foreground)]">{title}</div>
      <div className="max-w-[42ch] text-[13px] leading-relaxed text-[var(--muted)]">{body}</div>
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

/** A skeleton shaped like the content it replaces, not a generic spinner. */
export function Skeleton({ className }: { className?: string }) {
  return (
    <div
      className={cx("animate-pulse-dot rounded-[8px] bg-[var(--surface-2)]", className)}
      aria-hidden="true"
    />
  );
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-[10px] border border-[var(--danger)]/30 bg-[var(--danger)]/8 px-3.5 py-2.5 text-[13px] text-[var(--danger)]">
      {children}
    </div>
  );
}

/** Relative time, because "3m ago" is what an operator actually needs. */
export function relativeTime(iso: string | null): string {
  if (!iso) return "never";
  const seconds = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (seconds < 45) return "just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  if (seconds < 604800) return `${Math.floor(seconds / 86400)}d ago`;
  return new Date(iso).toLocaleDateString();
}

export function duration(seconds: number | null): string {
  if (seconds === null) return "—";
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h`;
  return `${Math.floor(seconds / 86400)}d`;
}
